<template>
  <div>
    <layout-header></layout-header>
    <layout-sidebar></layout-sidebar>
    <div class="page-wrapper">
      <div class="content mb-3">
        <div v-if="isLoading" class="dashboard-loading">
          <div class="spinner-border text-primary dashboard-spinner" role="status">
            <span class="visually-hidden">{{ $t('dashboardPage.loading') }}</span>
          </div>
        </div>

        <template v-else>
          <section class="dashboard-hero mb-3" :class="{ 'dashboard-hero--user': !isAdmin }">
            <div class="dashboard-hero__content">
              <span class="dashboard-hero__kicker">{{ isAdmin ? $t('dashboardPage.adminArea') : $t('dashboardPage.userArea') }}</span>
              <h2>{{ $t('dashboardPage.hello', { name: dashboardUserName }) }}</h2>
              <p>{{ isAdmin ? $t('dashboardPage.adminIntro') : $t('dashboardPage.userIntro') }}</p>
              <div class="quick-actions">
                <router-link v-for="action in quickActions" :key="action.to" :to="action.to" class="quick-action"><span><vue-feather :type="action.icon" /></span>{{ action.label }}</router-link>
              </div>
            </div>
            <div class="dashboard-hero__side">
              <div class="hero-date"><vue-feather type="calendar" /><div><small>{{ $t('dashboardPage.today') }}</small><strong>{{ currentDashboardDate }}</strong></div></div>
              <button class="hero-refresh" :disabled="isLoading" @click="loadDashboard"><vue-feather type="refresh-cw" />{{ $t('dashboardPage.refresh') }}</button>
            </div>
          </section>

          <!-- Summary Cards -->
          <div class="row g-3 mb-3">
            <div v-for="(card, index) in summaryCards" :key="card.label" class="col-xl-3 col-sm-6">
              <div class="card dashboard-stat-card border-0 h-100 card-hover card-fade-in"
                :class="`dashboard-stat-${card.tone || 'blue'}`"
                :style="`animation-delay: ${index * 0.1}s`">
                <div class="card-body p-3">
                  <div class="d-flex align-items-center">
                    <div class="flex-shrink-0 me-3">
                      <div class="icon-wrapper rounded icon-pulse">
                        <vue-feather :type="card.icon" size="19"></vue-feather>
                      </div>
                    </div>
                    <div class="flex-grow-1 min-w-0">
                      <p class="text-muted small mb-1 text-truncate">{{ card.label }}</p>
                      <h5 class="mb-0 text-truncate counter-animate">{{ card.displayValue }}</h5>
                    </div>
                    <span class="stat-arrow"><vue-feather type="arrow-up-right" /></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Abonnement Card -->
          <div v-if="isAdmin" class="row g-3 mb-3">
            <div class="col-12">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.4s">
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                    <div class="min-w-0 flex-grow-1">
                      <h6 class="mb-1">{{ dashboardStatusTitle }}</h6>
                      <small class="text-muted text-truncate d-block" v-if="dashboardStatusSubtitle">{{
                        dashboardStatusSubtitle }}</small>
                    </div>
                    <span v-if="hasAbonnementDetails" class="badge flex-shrink-0" :class="abonnementStatusBadgeClass">
                      {{ abonnementStatusLabel }}
                    </span>
                  </div>

                  <template v-if="isSingleApplicationMode">
                    <div class="row g-3">
                      <div class="col-md-4">
                        <small class="text-muted d-block mb-1">Mode</small>
                        <strong class="small d-block text-truncate">Entreprise unique</strong>
                      </div>
                      <div class="col-md-4">
                        <small class="text-muted d-block mb-1">Société</small>
                        <strong class="small d-block text-truncate">{{ abonnement?.societe_nom || "—" }}</strong>
                      </div>
                      <div class="col-md-4">
                        <small class="text-muted d-block mb-1">État</small>
                        <strong class="small d-block text-truncate">{{ abonnementStatusLabel }}</strong>
                      </div>
                      <div class="col-md-6">
                        <small class="text-muted d-block mb-1">Utilisateurs actifs</small>
                        <strong class="small d-block text-truncate">{{ formatNumber(abonnement?.utilisateurs?.actuels || 0)
                          }}</strong>
                      </div>
                      <div class="col-md-6">
                        <small class="text-muted d-block mb-1">Administrateurs</small>
                        <strong class="small d-block text-truncate">{{
                          formatNumber(abonnementAdministrateurs?.actuels || 0)
                          }}</strong>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="hasAbonnementDetails">
                    <div class="row g-3 mb-3">
                      <div class="col-md-4">
                        <small class="text-muted d-block mb-1">Formule</small>
                        <strong class="small d-block text-truncate">{{ abonnement.nom_abonnement || "—" }}</strong>
                      </div>
                      <div class="col-md-4">
                        <small class="text-muted d-block mb-1">Période</small>
                        <strong class="small d-block text-truncate">{{ abonnementDatesRange }}</strong>
                      </div>
                      <div class="col-md-4">
                        <small class="text-muted d-block mb-1">Temps restant</small>
                        <strong class="small d-block text-truncate">
                          {{
                            abonnement.dates?.jours_restants !== null &&
                              abonnement.dates?.jours_restants !== undefined
                              ? formatDaysRemaining(abonnement.dates?.jours_restants)
                              : "—"
                          }}
                        </strong>
                      </div>
                    </div>

                    <div class="row g-3">
                      <div class="col-lg-6">
                        <div class="d-flex justify-content-between align-items-center mb-1 gap-2">
                          <small class="fw-semibold text-truncate">Utilisateurs</small>
                          <small class="text-muted flex-shrink-0">
                            {{ abonnement.utilisateurs?.actuels || 0 }} /
                            {{ abonnement.utilisateurs?.limite ?? "∞" }}
                          </small>
                        </div>
                        <div class="progress" style="height: 6px;">
                          <div class="progress-bar bg-primary" :style="{ width: `${abonnementUsersPercent}%` }"></div>
                        </div>
                      </div>
                      <div class="col-lg-6">
                        <div class="d-flex justify-content-between align-items-center mb-1 gap-2">
                          <small class="fw-semibold text-truncate">Administrateurs</small>
                          <small class="text-muted flex-shrink-0">
                            {{ abonnementAdministrateurs?.actuels || 0 }} /
                            {{ abonnementAdministrateurs?.limite ?? "∞" }}
                          </small>
                        </div>
                        <div class="progress" style="height: 6px;">
                          <div class="progress-bar bg-info" :style="{ width: `${abonnementAdminsPercent}%` }"></div>
                        </div>
                      </div>
                    </div>

                    <div v-if="(abonnement.alertes || []).length" class="mt-3 pt-3 border-top">
                      <ul class="list-unstyled mb-0">
                        <li v-for="(alerte, index) in abonnement.alertes || []" :key="index" :class="[
                          'd-flex align-items-start small mb-1',
                          alerte.type === 'danger' ? 'text-danger' : 'text-warning',
                        ]">
                          <vue-feather type="alert-triangle" size="14" class="me-2 flex-shrink-0 mt-1"></vue-feather>
                          <span class="text-break">{{ alerte.message }}</span>
                        </li>
                      </ul>
                    </div>
                  </template>
                  <p v-else class="text-muted small mb-0">
                    {{ abonnement?.message || "Aucun abonnement actif." }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Revenue Chart (Moved Up) -->
          <div v-if="isAdmin" class="row g-3 mb-3">
            <div class="col-12">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.5s">
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                      <h6 class="mb-1">Évolution du chiffre d’affaires</h6>
                      <small v-if="revenueChartSeries.length" class="text-muted">
                        Total affiché : {{ formatCurrency(revenueChartTotal) }}
                      </small>
                    </div>
                    <div class="revenue-chart-controls">
                      <label><span>Période</span>
                        <select v-model="revenueMonths" class="form-select form-select-sm" @change="reloadRevenueChart">
                          <option v-for="month in 12" :key="month" :value="String(month)">{{ month }} mois</option>
                          <option value="all">Toute la période</option>
                        </select>
                      </label>
                      <label><span>Affichage</span>
                        <select v-model="revenueGranularity" class="form-select form-select-sm" @change="reloadRevenueChart">
                          <option value="day">Par jour</option>
                          <option value="week">Par semaine</option>
                          <option value="month">Par mois</option>
                        </select>
                      </label>
                    </div>
                  </div>
                  <div v-if="revenueChartLoading" class="revenue-chart-loading"><span class="spinner-border spinner-border-sm text-primary"></span></div>
                  <ApexChart v-else-if="revenueChartSeries.length" type="area" height="290" :options="revenueChartOptions"
                    :series="revenueChartSeries" />
                  <p v-else class="text-muted small mb-0">
                    Aucune donnée disponible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- KPIs & Payment Snapshot -->
          <div v-if="isAdmin" class="row g-3 mb-3">
            <div :class="isAdmin ? 'col-lg-8' : 'col-12'">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.6s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Indicateurs clés</h6>
                  <div class="row g-2">
                    <div v-for="kpi in kpiCards" :key="kpi.label" class="col-md-6 col-sm-6">
                      <div class="p-2 bg-light rounded kpi-card">
                        <small class="text-muted d-block mb-1 text-truncate">{{ kpi.label }}</small>
                        <strong class="small d-block text-truncate">{{ kpi.displayValue }}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-4">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.7s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Paiement des factures</h6>
                  <div v-if="paymentSummaryCards.length" class="d-flex flex-column gap-2">
                    <div
                      v-for="card in paymentSummaryCards"
                      :key="card.label"
                      class="p-3 rounded payment-summary-card"
                      :class="card.className"
                    >
                      <small class="d-block mb-1 text-truncate">{{ card.label }}</small>
                      <strong class="d-block mb-1">{{ card.displayValue }}</strong>
                      <small class="text-muted d-block">{{ card.caption }}</small>
                    </div>
                  </div>
                  <p v-else class="text-muted small mb-0">
                    Aucune donnée disponible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Operations & Financial Summary -->
          <div class="row g-3 mb-3">
            <div class="col-lg-8">
              <div class="card border-0 shadow-sm card-hover card-fade-in" style="animation-delay: 0.8s">
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h6 class="mb-0">Tableau opérationnel</h6>
                    <small class="text-muted" v-if="operationsSnapshot && operationsSnapshot.generated_at">
                      {{ formatDate(operationsSnapshot.generated_at) }}
                    </small>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm table-hover mb-0">
                      <thead class="table-light">
                        <tr>
                          <th class="small">Indicateur</th>
                          <th class="text-end small">Total</th>
                          <th class="text-end small">Mois</th>
                          <th class="text-end small">Semaine</th>
                          <th v-if="isAdmin" class="text-end small">Montant</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!operationsMetrics.length">
                          <td colspan="5" class="text-center text-muted py-3 small">
                            Aucune donnée disponible.
                          </td>
                        </tr>
                        <tr v-for="metric in operationsMetrics" :key="metric.key">
                          <td class="small text-truncate" style="max-width: 150px;">{{ metric.label }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(metric.total) }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(metric.this_month) }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(metric.this_week) }}</td>
                          <td v-if="isAdmin" class="text-end small text-truncate" style="max-width: 100px;">
                            {{ formatMetricAmount(metric.amount_total) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="isAdmin" class="col-lg-4">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 0.9s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Performance financière</h6>
                  <template v-if="financialSummary">
                    <div v-if="financialDonutSeries.length">
                      <ApexChart type="donut" height="180" :options="financialDonutOptions"
                        :series="financialDonutSeries" />
                    </div>
                    <div class="mt-3">
                      <div class="mb-2 p-2 bg-light rounded financial-card">
                        <div class="d-flex justify-content-between mb-1 gap-2">
                          <small class="text-muted text-truncate">Mois en cours</small>
                          <strong class="small text-truncate">{{ formatCurrency(financialSummary.current_month?.amount)
                            }}</strong>
                        </div>
                        <small class="text-muted d-block mb-1">
                          {{ formatNumberOrDash(financialSummary.current_month?.count) }} facture(s)
                        </small>
                        <small :class="trendClass(financialSummary.month_variation_percent)">
                          {{ formatTrendPercent(financialSummary.month_variation_percent) }}
                        </small>
                      </div>
                      <div class="d-flex flex-column gap-2">
                        <div class="d-flex justify-content-between align-items-center gap-2">
                          <small class="text-muted text-truncate">Facture moy.</small>
                          <strong class="small text-truncate">{{ formatCurrency(financialSummary.average_invoice_amount)
                            }}</strong>
                        </div>
                      </div>
                    </div>
                  </template>
                  <p v-else class="text-muted small mb-0">
                    Aucune donnée disponible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Top Clients & Stock Alerts -->
          <div class="row g-3 mb-3">
            <div v-if="isAdmin" class="col-lg-6">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 1s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Top clients</h6>
                  <div class="table-responsive">
                    <table class="table table-sm table-hover mb-0">
                      <thead class="table-light">
                        <tr>
                          <th class="small">Client</th>
                          <th class="text-end small">Factures</th>
                          <th class="text-end small">Total</th>
                          <th class="text-end small">Attente</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!topClientsRows.length">
                          <td colspan="4" class="text-center text-muted py-3 small">
                            Aucune donnée disponible.
                          </td>
                        </tr>
                        <tr v-for="client in topClientsRows" :key="client.client_id">
                          <td class="small text-truncate" style="max-width: 150px;">{{ formatTopClientName(client) }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(client.factures_count) }}</td>
                          <td class="text-end small text-truncate" style="max-width: 100px;">{{
                            formatCurrency(client.total_amount) }}</td>
                          <td class="text-end small text-truncate" style="max-width: 100px;">
                            <span :class="client.outstanding_amount > 0 ? 'text-danger fw-semibold' : ''">
                              {{ formatCurrency(client.outstanding_amount) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div :class="isAdmin ? 'col-lg-6' : 'col-12'">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 1.1s">
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h6 class="mb-0">Alertes stock</h6>
                    <div v-if="stockAlertsSummary" class="d-flex gap-2 flex-wrap">
                      <span class="badge bg-danger-subtle text-danger small alert-badge">{{
                        formatNumberOrDash(stockAlertsSummary.critical) }}</span>
                      <span class="badge bg-warning-subtle text-warning small">{{
                        formatNumberOrDash(stockAlertsSummary.low)
                        }}</span>
                      <span class="badge bg-info-subtle text-info small">{{
                        formatNumberOrDash(stockAlertsSummary.below_optimal)
                        }}</span>
                    </div>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm table-hover mb-0">
                      <thead class="table-light">
                        <tr>
                          <th class="small">Produit</th>
                          <th class="text-end small">Stock</th>
                          <th class="text-end small">Seuil</th>
                          <th class="small">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!stockAlertsItems.length">
                          <td colspan="4" class="text-center text-muted py-3 small">
                            Aucune alerte.
                          </td>
                        </tr>
                        <tr v-for="item in stockAlertsItems" :key="item.product_id">
                          <td class="small text-truncate" style="max-width: 120px;">{{ item.name }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(item.current_stock) }}</td>
                          <td class="text-end small">{{ formatNumberOrDash(item.threshold) }}</td>
                          <td>
                            <span class="badge small" :class="stockStatusBadgeClass(item.status)">
                              {{ stockStatusLabel(item.status) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Activity -->
          <div class="row g-3">
            <div class="col-lg-6">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 1.2s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Dernières factures</h6>
                  <div v-if="recentFactures.length === 0" class="text-muted small">
                    Aucune facture récente.
                  </div>
                  <div v-else class="d-flex flex-column gap-2">
                      <div v-for="facture in recentFactures" :key="facture.id"
                      class="d-flex justify-content-between align-items-center p-2 bg-light rounded gap-2 activity-item">
                      <div class="min-w-0 flex-grow-1">
                        <strong class="small d-block text-truncate">#{{ facture.numero_facture || facture.id }}</strong>
                        <small class="text-muted text-truncate d-block">
                          {{ formatFactureClientName(facture) }} • {{ formatDate(facture.date_creation || facture.created_at) }}
                        </small>
                      </div>
                      <strong v-if="isAdmin" class="small flex-shrink-0">{{ formatCurrency(facture.montant_total) }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-lg-6">
              <div class="card border-0 shadow-sm h-100 card-hover card-fade-in" style="animation-delay: 1.3s">
                <div class="card-body p-3">
                  <h6 class="mb-3">Nouveaux clients</h6>
                  <div v-if="recentClients.length === 0" class="text-muted small">
                    Aucun client récent.
                  </div>
                  <div v-else class="d-flex flex-column gap-2">
                    <div v-for="client in recentClients" :key="client.id"
                      class="d-flex justify-content-between align-items-center p-2 bg-light rounded gap-2 activity-item">
                      <div class="min-w-0 flex-grow-1">
                        <strong class="small d-block text-truncate">{{ client.nom }} {{ client.prenom }}</strong>
                        <small class="text-muted text-truncate d-block">{{ client.email || "Email non renseigné"
                          }}</small>
                      </div>
                      <small class="text-muted flex-shrink-0">{{ formatDate(client.created_at) }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getDashboardSummary,
  getDashboardKPIs,
  getDashboardFactureStats,
  getDashboardRecentActivity,
  getDashboardAbonnementStats,
  getDashboardOperations,
  getDashboardFinancials,
  getDashboardTopClients,
  getDashboardStockAlerts,
} from "@/services/dashboardService";
import { toast } from "vue3-toastify";
import { getCurrentUser } from "@/services/authService";

export default {
  name: "PosDashboard",
  data() {
    return {
      isLoading: false,
      currentUser: getCurrentUser(),
      summary: null,
      kpis: null,
      factureStats: null,
      revenueMonths: "12",
      revenueGranularity: "month",
      revenueChartLoading: false,
      abonnement: null,
      operationsSnapshot: null,
      financialTrends: null,
      topClients: null,
      stockAlerts: null,
      activity: {
        dernieres_factures: [],
        derniers_clients: [],
      },
    };
  },
  computed: {
    isAdmin() {
      return String(this.currentUser?.role || "").toLowerCase() === "admin";
    },
    dashboardUserName() {
      return this.currentUser?.nom_utilisateur || this.currentUser?.name || (this.isAdmin ? "Administrateur" : "Utilisateur");
    },
    quickActions() {
      const actions = [
        { to: "/devis", label: this.$t("dashboardPage.newQuote"), icon: "clipboard" },
        { to: "/factures", label: this.$t("dashboardPage.invoices"), icon: "file-text" },
        { to: "/ventes-unitaires", label: this.$t("dashboardPage.unitSale"), icon: "shopping-bag" },
        { to: "/tiers", label: this.$t("dashboardPage.clients"), icon: "users" },
      ];
      if (this.isAdmin) actions.push({ to: "/stocks", label: this.$t("dashboardPage.stock"), icon: "package" });
      return actions;
    },
    summaryCards() {
      const s = this.summary || {};
      if (!this.isAdmin) {
        const operations = this.operationsMetrics;
        const devis = operations.find((item) => item.key === "devis");
        const deliveries = operations.find((item) => item.key === "bon_livraison");
        return [
          { label: "Clients", displayValue: this.formatNumber(s.nombre_clients || 0), icon: "users" },
          { label: "Factures", displayValue: this.formatNumber(s.nombre_factures || 0), icon: "file-text" },
          { label: "Devis en cours", displayValue: this.formatNumber(devis?.total || 0), icon: "clipboard" },
          { label: "Livraisons en attente", displayValue: this.formatNumber(deliveries?.extra?.en_attente || 0), icon: "truck" },
        ];
      }
      return [
        { label: "Clients", value: s.nombre_clients || 0, displayValue: this.formatNumber(s.nombre_clients || 0), icon: "users", tone: "blue" },
        { label: "Factures", value: s.nombre_factures || 0, displayValue: this.formatNumber(s.nombre_factures || 0), icon: "file-text", tone: "violet" },
        { label: "Montant facturé", value: s.total_factures || 0, displayValue: this.formatCurrency(s.total_factures || 0), icon: "trending-up", tone: "green" },
        { label: "Taux de paiement", value: s.taux_paiement || 0, displayValue: this.formatPercent(s.taux_paiement || 0), icon: "percent", tone: "orange" },
      ];
    },
    currentDashboardDate() {
      const locale = this.$i18n.locale === "ar" ? "ar-MA" : this.$i18n.locale === "en" ? "en-GB" : "fr-MA";
      return new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(new Date());
    },
    kpiCards() {
      const k = this.kpis || {};
      return [
        { label: "CA mensuel", displayValue: this.formatCurrency(k.ca_mensuel || 0) },
        { label: "Croissance mensuelle", displayValue: this.formatPercent(k.croissance_mensuelle || 0) },
        { label: "Nouveaux clients", displayValue: this.formatNumber(k.nouveaux_clients_mois || 0) },
        { label: "Nouveaux devis", displayValue: this.formatNumber(k.nouveaux_devis_mois || 0) },
        { label: "Devis en attente", displayValue: this.formatNumber(k.devis_en_attente || 0) },
        { label: "Montant impayé", displayValue: this.formatCurrency(k.montant_impaye || 0) },
      ];
    },
    financialDonutSeries() {
      const f = this.financialSummary;
      if (!f) return [];
      const series = [
        Number(f.paid_amount) || 0,
        Number(f.partial_amount) || 0,
        Number(f.unpaid_amount) || 0,
      ];

      return series.some((value) => value > 0) ? series : [];
    },
    financialDonutOptions() {
      return {
        chart: { type: 'donut' },
        labels: ['Encaissé', 'Reste partiel', 'Impayé'],
        colors: ['#10AC84', '#FFA502', '#EE5A6F'],
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                name: { show: false },
                value: { show: false },
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '12px',
                  fontWeight: 700,
                  offsetY: 6,
                  formatter: () => {
                    const total = this.financialDonutSeries.reduce((sum, val) => sum + val, 0);
                    return this.formatCurrency(total).replace(' MAD', '');
                  }
                }
              }
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val) => this.formatCurrency(val)
          }
        }
      };
    },
    operationsMetrics() {
      const metrics = Array.isArray(this.operationsSnapshot?.metrics)
        ? this.operationsSnapshot.metrics
        : [];

      const normalized = metrics.map((metric) => ({
        key: metric.key,
        label: metric.label,
        total: metric.total ?? null,
        this_month: metric.this_month ?? null,
        this_week: metric.this_week ?? null,
        amount_total: metric.amount_total ?? null,
        amount_month: metric.amount_month ?? null,
        amount_week: metric.amount_week ?? null,
        paid_amount: metric.paid_amount ?? null,
        outstanding_amount: metric.outstanding_amount ?? null,
        extra: metric.extra ?? null,
      }));

      const byKey = new Map(normalized.map((m) => [m.key, m]));
      const defaults = [
        { key: 'clients', label: 'Clients' },
        { key: 'devis', label: 'Devis' },
        { key: 'factures', label: 'Factures' },
        { key: 'bon_livraison', label: 'Bons de livraison' },
      ];

      return defaults.map((def) => ({
        key: def.key,
        label: def.label,
        total: 0,
        this_month: 0,
        this_week: 0,
        amount_total: null,
        amount_month: null,
        amount_week: null,
        paid_amount: null,
        outstanding_amount: null,
        extra: null,
        ...(byKey.get(def.key) || {}),
      }));
    },
    financialSummary() {
      return this.financialTrends?.data || null;
    },
    paymentSummaryCards() {
      const summary = this.financialSummary;

      if (!summary) return [];

      return [
        {
          label: "Factures payées complètement",
          displayValue: this.formatCurrency(summary.fully_paid_amount || 0),
          caption: `${this.formatNumberOrDash(summary.fully_paid_count || 0)} facture(s) soldée(s)`,
          className: "bg-success-subtle",
        },
        {
          label: "Total non payé",
          displayValue: this.formatCurrency(this.totalUnpaidAmount),
          caption: `${this.formatNumberOrDash(summary.total_unpaid_count || 0)} facture(s) avec reste à payer`,
          className: "bg-danger-subtle",
        },
        {
          label: "Montant encaissé",
          displayValue: this.formatCurrency(summary.paid_amount || 0),
          caption: "Inclut les paiements partiels",
          className: "bg-primary-subtle",
        },
      ];
    },
    totalUnpaidAmount() {
      const summary = this.financialSummary;

      if (!summary) return 0;

      if (summary.total_unpaid_amount !== null && summary.total_unpaid_amount !== undefined) {
        return Number(summary.total_unpaid_amount) || 0;
      }

      return (Number(summary.partial_amount) || 0) + (Number(summary.unpaid_amount) || 0);
    },
    topClientsRows() {
      if (Array.isArray(this.topClients?.data)) return this.topClients.data;
      if (Array.isArray(this.topClients)) return this.topClients;
      return [];
    },
    stockAlertsSummary() {
      return this.stockAlerts?.summary || null;
    },
    stockAlertsItems() {
      return Array.isArray(this.stockAlerts?.items) ? this.stockAlerts.items : [];
    },
    isSingleApplicationMode() {
      return Boolean(this.abonnement?.single_application_mode);
    },
    hasAbonnementDetails() {
      if (!this.abonnement) return false;
      const a = this.abonnement;
      return !!(a.nom_abonnement || a.statut || typeof a.abonnement_actif === "boolean" ||
        (a.dates && (a.dates.debut || a.dates.fin)) ||
        (a.utilisateurs && (a.utilisateurs.actuels !== undefined || a.utilisateurs.limite !== undefined)) ||
        (Array.isArray(a.alertes) && a.alertes.length));
    },
    dashboardStatusTitle() {
      return this.isSingleApplicationMode ? "Application" : "Abonnement";
    },
    dashboardStatusSubtitle() {
      if (this.isSingleApplicationMode) {
        return this.abonnement?.message || null;
      }
      return this.abonnementPeriodText;
    },
    abonnementStatusLabel() {
      const status = this.abonnement?.statut;
      switch (status) {
        case "actif": return "Actif";
        case "à_venir":
        case "a_venir": return "À venir";
        case "expiré":
        case "expire": return "Expiré";
        default: return this.abonnement?.abonnement_actif ? "Actif" : "Non défini";
      }
    },
    abonnementStatusBadgeClass() {
      const status = this.abonnement?.statut;
      if (status === "actif") return "bg-success-subtle text-success";
      if (status === "à_venir" || status === "a_venir") return "bg-warning-subtle text-warning";
      if (status === "expiré" || status === "expire") return "bg-danger-subtle text-danger";
      return "bg-secondary-subtle text-muted";
    },
    abonnementPeriodText() {
      const dates = this.abonnement?.dates;
      if (!dates) return null;
      const start = dates.debut ? this.formatDate(dates.debut) : null;
      const end = dates.fin ? this.formatDate(dates.fin) : null;
      if (start && end) return `Du ${start} au ${end}`;
      if (start) return `À partir du ${start}`;
      if (end) return `Jusqu'au ${end}`;
      return null;
    },
    abonnementDatesRange() {
      const dates = this.abonnement?.dates;
      if (!dates) return "—";
      const start = dates.debut ? this.formatDate(dates.debut) : "—";
      const end = dates.fin ? this.formatDate(dates.fin) : "—";
      return `${start} → ${end}`;
    },
    abonnementUsersPercent() {
      const limit = Number(this.abonnement?.utilisateurs?.limite ?? 0);
      const current = Number(this.abonnement?.utilisateurs?.actuels ?? 0);
      if (limit <= 0) return current > 0 ? 100 : 0;
      return Math.max(0, Math.min(Math.round((current / limit) * 100), 100));
    },
    abonnementAdministrateurs() {
      if (this.abonnement?.administrateurs) return this.abonnement.administrateurs;
      return {
        actuels: this.abonnement?.administrateurs_actuels ?? this.abonnement?.admin_actuels ?? 0,
        limite: this.abonnement?.administrateurs_limite ?? this.abonnement?.admin_limite ?? null,
      };
    },
    abonnementAdminsPercent() {
      const admins = this.abonnementAdministrateurs || {};
      const limit = Number(admins.limite ?? 0);
      const current = Number(admins.actuels ?? 0);
      if (limit <= 0) return current > 0 ? 100 : 0;
      return Math.max(0, Math.min(Math.round((current / limit) * 100), 100));
    },
    revenueChartSeries() {
      const entries = (this.factureStats?.ca_series || this.factureStats?.ca_par_mois || []).map((item) => ({
        periode: item.periode || item.mois,
        montant: Number(item.montant) || 0,
      }));
      if (!entries.length) return [];
      return [{ name: "Chiffre d'affaires", data: entries.map((item) => item.montant) }];
    },
    revenueChartOptions() {
      const entries = this.factureStats?.ca_series || this.factureStats?.ca_par_mois || [];
      const categories = entries.map((item) => this.formatRevenuePeriod(item.periode || item.mois));
      return {
        chart: {
          toolbar: { show: true, tools: { download: false, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
          zoom: { enabled: true, type: "x", autoScaleYaxis: true },
        },
        colors: ["#2E86DE"],
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { categories, labels: { style: { colors: "#6B7280", fontSize: "11px" } } },
        yaxis: {
          labels: {
            style: { fontSize: "11px" },
            formatter: (val) => {
              if (val === null || val === undefined) return "";
              return this.formatCurrency(val).replace(/\u00a0MAD$/, "");
            },
          },
        },
        tooltip: { y: { formatter: (val) => this.formatCurrency(val) } },
        fill: {
          type: "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            shadeIntensity: 0.3,
            inverseColors: false,
            opacityFrom: 0.5,
            opacityTo: 0.1,
          },
        },
        markers: { size: 3, strokeWidth: 2, hover: { size: 5 } },
        grid: { strokeDashArray: 4, borderColor: "#E5E7EB" },
      };
    },
    revenueChartTotal() {
      return this.revenueChartSeries[0]?.data.reduce((sum, value) => sum + (Number(value) || 0), 0) || 0;
    },
    recentFactures() {
      return this.activity?.dernieres_factures || [];
    },
    recentClients() {
      return this.activity?.derniers_clients || [];
    },
  },
  methods: {
    async reloadRevenueChart() {
      if (!this.isAdmin) return;
      this.revenueChartLoading = true;
      try {
        this.factureStats = await getDashboardFactureStats({
          months: this.revenueMonths,
          granularity: this.revenueGranularity,
        });
      } catch (error) {
        toast.error(error?.response?.data?.message || "Impossible de charger le graphique.");
      } finally {
        this.revenueChartLoading = false;
      }
    },
    formatRevenuePeriod(value) {
      if (!value) return "";
      if (this.revenueGranularity === "month") return this.formatMonth(value);
      const date = new Date(`${value}T00:00:00`);
      if (Number.isNaN(date.getTime())) return value;
      const formatted = new Intl.DateTimeFormat("fr-MA", { day: "2-digit", month: "short" }).format(date);
      return this.revenueGranularity === "week" ? `Sem. ${formatted}` : formatted;
    },
    async loadDashboard() {
      this.isLoading = true;
      const requests = [
        ["summary", getDashboardSummary],
        ["activity", getDashboardRecentActivity],
        ["operationsSnapshot", getDashboardOperations],
        ["stockAlerts", getDashboardStockAlerts],
      ];

      if (this.isAdmin) {
        requests.push(
          ["kpis", getDashboardKPIs],
          ["factureStats", () => getDashboardFactureStats({ months: this.revenueMonths, granularity: this.revenueGranularity })],
          ["abonnement", getDashboardAbonnementStats],
          ["financialTrends", getDashboardFinancials],
          ["topClients", getDashboardTopClients],
        );
      }

      const errorSet = new Set();
      try {
        const results = await Promise.allSettled(requests.map(([, fn]) => fn()));
        results.forEach((result, index) => {
          const [key] = requests[index];
          if (result.status === "fulfilled") {
            const payload = result.value;
            if (payload && typeof payload === "object" && payload.success === false && payload.message) {
              errorSet.add(payload.message);
            }
            this[key] = payload;
          } else {
            const reason = result.reason;
            const apiMessage =
              reason?.response?.data?.message ||
              reason?.response?.data?.error ||
              reason?.message;
            errorSet.add(apiMessage || `Erreur lors du chargement de ${this.humanizeKey(key)}`);
            console.error(`Dashboard - ${key} failed`, reason);
          }
        });
      } finally {
        this.isLoading = false;
        if (errorSet.size) {
          const messages = Array.from(errorSet);
          toast.error(messages.length === 1 ? messages[0] : `Erreur de chargement du tableau de bord.\n${messages.join("\n")}`);
        }
      }
    },
    humanizeKey(key) {
      const mapping = {
        summary: "la synthèse",
        kpis: "les indicateurs",
        factureStats: "les statistiques factures",
        activity: "l'activité récente",
        abonnement: "l'abonnement",
        operationsSnapshot: "le tableau opérationnel",
        financialTrends: "les performances financières",
        topClients: "les meilleurs clients",
        stockAlerts: "les alertes stock",
      };
      return mapping[key] || key;
    },
    formatTopClientName(client) {
      if (!client) return "Client";

      const companyName = String(client.client_name || client.nom_societe || client.company_name || "").trim();
      if (companyName) return companyName;

      const fullName = [
        client.prenom || client.first_name,
        client.nom || client.last_name || client.name,
      ]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (fullName) return fullName;

      const contact = String(client.email || client.telephone || "").trim();
      if (contact) return contact;

      return client.client_id ? `Client #${client.client_id}` : "Client";
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return "0,00\u00a0MAD";
      try {
        return new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "MAD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value));
      } catch (e) {
        return `${Number(value).toFixed(2)} MAD`;
      }
    },
    formatNumber(value) {
      return new Intl.NumberFormat("fr-FR").format(value || 0);
    },
    formatPercent(value) {
      return `${(Number(value) || 0).toFixed(2)}\u00a0%`;
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Intl.DateTimeFormat("fr-FR", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        }).format(new Date(value));
      } catch (e) {
        return value;
      }
    },
    formatMonth(value) {
      if (!value) return "—";
      const [year, month] = value.split("-");
      try {
        return new Intl.DateTimeFormat("fr-FR", {
          month: "short",
          year: "numeric",
        }).format(new Date(Number(year), Number(month) - 1));
      } catch (e) {
        return value;
      }
    },
    formatNumberOrDash(value) {
      if (value === null || value === undefined) return "—";
      return this.formatNumber(value);
    },
    formatTrendPercent(value) {
      if (value === null || value === undefined) return "—";
      const number = Number(value);
      const sign = number > 0 ? "+" : "";
      return `${sign}${number.toFixed(2)} %`;
    },
    trendClass(value) {
      if (value === null || value === undefined) return "text-muted";
      if (value > 0) return "text-success";
      if (value < 0) return "text-danger";
      return "text-muted";
    },
    formatMetricAmount(value) {
      if (value === null || value === undefined) return "—";
      return this.formatCurrency(value);
    },
    formatFactureClientName(facture) {
      const client = facture?.commande?.client;
      if (!client) return facture?.client_name || "Client inconnu";

      if (client.nom_societe) return client.nom_societe;

      const fullName = [client.prenom, client.nom].filter(Boolean).join(" ").trim();
      return fullName || facture?.client_name || "Client inconnu";
    },
    formatDaysRemaining(value) {
      if (value === null || value === undefined) return "durée inconnue";
      const days = Number(value);
      if (Number.isNaN(days)) return "durée inconnue";
      if (days > 0) return `${days} jour${days > 1 ? "s" : ""}`;
      if (days === 0) return "Aujourd'hui";
      const abs = Math.abs(days);
      return `Expiré (${abs}j)`;
    },
    stockStatusLabel(status) {
      switch (status) {
        case "out": return "Critique";
        case "low": return "Faible";
        case "below-optimal": return "Sous opt.";
        default: return "OK";
      }
    },
    stockStatusBadgeClass(status) {
      switch (status) {
        case "out": return "bg-danger";
        case "low": return "bg-warning text-dark";
        case "below-optimal": return "bg-info text-dark";
        default: return "bg-success";
      }
    },
  },
  mounted() {
    this.loadDashboard();
  },
};
</script>

<style scoped>
.content {
  background: #f5f7fb;
  min-height: calc(100vh - 60px);
}

.dashboard-hero {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 28px;
  padding: 28px 30px;
  border-radius: 18px;
  overflow: hidden;
  color: #fff;
  background: linear-gradient(120deg, #092c4c 0%, #155f91 68%, #2d8fc1 100%);
  box-shadow: 0 12px 30px rgba(9, 44, 76, .16);
}

.dashboard-hero::after {
  content: '';
  position: absolute;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  inset-inline-end: -90px;
  bottom: -180px;
  background: rgba(255,255,255,.09);
}

.dashboard-hero--user {
  background: linear-gradient(120deg, #163d5d 0%, #236d80 100%);
}

.dashboard-hero__content,
.dashboard-hero__side {
  position: relative;
  z-index: 1;
}

.dashboard-hero__content {
  min-width: 0;
}

.dashboard-hero__kicker {
  display: block;
  margin-bottom: 5px;
  color: #bcdcf2;
  font-size: .72rem;
  font-weight: 750;
  letter-spacing: .11em;
  text-transform: uppercase;
}

.dashboard-hero h2 {
  margin: 0 0 5px;
  color: #fff;
  font-size: 1.75rem;
}

.dashboard-hero p {
  margin: 0;
  color: rgba(255,255,255,.78);
}

.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 20px;
}

.quick-action {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 11px 7px 8px;
  border: 1px solid rgba(255,255,255,.18);
  border-radius: 9px;
  color: #fff;
  background: rgba(255,255,255,.09);
  font-size: .78rem;
  font-weight: 650;
  transition: .2s ease;
}

.quick-action:hover {
  color: #092c4c;
  background: #fff;
  transform: translateY(-2px);
}

.quick-action span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 7px;
  background: rgba(255,255,255,.13);
}

.quick-action svg {
  width: 14px;
}

.dashboard-hero__side {
  min-width: 250px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 9px;
}

.hero-date,
.hero-refresh {
  display: flex;
  align-items: center;
  gap: 11px;
  border: 1px solid rgba(255,255,255,.17);
  border-radius: 11px;
  background: rgba(255,255,255,.09);
  color: #fff;
}

.hero-date {
  padding: 12px 14px;
}

.hero-date svg,
.hero-refresh svg {
  width: 17px;
}

.hero-date small,
.hero-date strong {
  display: block;
}

.hero-date small {
  color: #bcdcf2;
  font-size: .67rem;
  text-transform: uppercase;
}

.hero-date strong {
  margin-top: 2px;
  font-size: .8rem;
}

.hero-refresh {
  justify-content: center;
  padding: 9px;
  cursor: pointer;
  transition: .2s ease;
}

.hero-refresh:hover {
  background: rgba(255,255,255,.18);
}

.stat-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-inline-start: auto;
  border-radius: 50%;
  color: #98a2b3;
  background: #f5f7fa;
}

.stat-arrow svg {
  width: 14px;
}

/* Loading state */
.dashboard-loading {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-spinner {
  width: 3.5rem;
  height: 3.5rem;
  border-width: 0.35rem;
}

/* Fade-in animation for cards */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-fade-in {
  animation: fadeInUp 0.6s ease-out forwards;
  opacity: 0;
}

/* Hover effect for cards */
.card-hover {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(9, 44, 76, 0.10) !important;
}

/* Icon pulse animation */
@keyframes iconPulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }
}

.icon-pulse {
  animation: iconPulse 2s ease-in-out infinite;
  transition: all 0.3s ease;
}

.card-hover:hover .icon-pulse {
  animation: none;
  transform: scale(1.1) rotate(5deg);
}

/* Counter animation */
.counter-animate {
  transition: all 0.3s ease;
}

/* Progress bar animation */
.progress-bar {
  transition: width 1s ease-in-out;
  animation: progressFill 1.5s ease-in-out;
}

@keyframes progressFill {
  from {
    width: 0;
  }
}

/* KPI card hover */
.kpi-card {
  transition: all 0.3s ease;
}

.kpi-card:hover {
  background-color: #e3f2fd !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Financial card hover */
.financial-card {
  transition: all 0.3s ease;
}

.financial-card:hover {
  background-color: #e8f5e9 !important;
  transform: scale(1.02);
}

/* Legend item hover */
.legend-item {
  transition: all 0.2s ease;
  padding: 4px 8px;
  border-radius: 4px;
}

.legend-item:hover {
  background-color: rgba(46, 134, 222, 0.05);
}

/* Activity item animation */
.activity-item {
  transition: all 0.3s ease;
}

.activity-item:hover {
  background-color: #fff3cd !important;
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Table row hover */
.table-hover tbody tr {
  transition: all 0.2s ease;
}

.table-hover tbody tr:hover {
  background-color: rgba(46, 134, 222, 0.05);
  transform: scale(1.01);
}

/* Badge pulse for alerts */
@keyframes badgePulse {

  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4);
  }

  50% {
    box-shadow: 0 0 0 8px rgba(220, 53, 69, 0);
  }
}

.alert-badge {
  animation: badgePulse 2s infinite;
}

/* Smooth loading animation */
.spinner-border {
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

/* Chart container fade-in */
.apexcharts-canvas {
  animation: fadeIn 0.8s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* Button hover effect */
.btn {
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.btn:hover::before {
  width: 300px;
  height: 300px;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn:active {
  transform: translateY(0);
}

.icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.progress {
  background-color: #e9ecef;
  overflow: hidden;
}

.card {
  transition: box-shadow 0.2s ease;
  border-radius: 12px;
  overflow: hidden;
}

.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  transition: all 0.3s ease;
}

.badge:hover {
  transform: scale(1.05);
}

.bg-primary-subtle {
  background-color: rgba(46, 134, 222, 0.1) !important;
}

.bg-light {
  color: #152D4B !important;

}

.bg-success-subtle {
  background-color: rgba(40, 167, 69, 0.1) !important;
}

.bg-warning-subtle {
  background-color: rgba(255, 193, 7, 0.1) !important;
}

.bg-danger-subtle {
  background-color: rgba(220, 53, 69, 0.1) !important;
}

.bg-info-subtle {
  background-color: rgba(23, 162, 184, 0.1) !important;
}

.bg-secondary-subtle {
  background-color: rgba(108, 117, 125, 0.1) !important;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  transition: all 0.3s ease;
}

.legend-dot:hover {
  transform: scale(1.5);
}

.min-w-0 {
  min-width: 0;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-break {
  word-wrap: break-word;
  word-break: break-word;
}

.table td,
.table th {
  vertical-align: middle;
}

.admin-welcome {
  align-items: center;
  background: linear-gradient(120deg, #102a43 0%, #1f5f8b 62%, #2d7fb1 100%);
  border-radius: 18px;
  box-shadow: 0 14px 32px rgba(16, 42, 67, 0.18);
  color: #fff;
  display: flex;
  justify-content: space-between;
  overflow: hidden;
  padding: 24px 28px;
  position: relative;
}

.admin-welcome::after {
  background: rgba(255, 255, 255, 0.07);
  border-radius: 50%;
  content: "";
  height: 210px;
  position: absolute;
  right: -55px;
  top: -105px;
  width: 210px;
}

.admin-welcome-kicker {
  color: #b9ddf5;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .11em;
  text-transform: uppercase;
}

.admin-welcome h3 {
  color: #fff !important;
  font-size: 24px;
  font-weight: 750;
  margin: 5px 0 4px;
}

.admin-welcome p {
  color: rgba(255, 255, 255, 0.75);
  margin: 0;
}

.admin-welcome-date {
  align-items: center;
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 13px;
  display: flex;
  gap: 11px;
  min-width: 210px;
  padding: 12px 15px;
  position: relative;
  z-index: 1;
}

.admin-welcome-date small,
.admin-welcome-date strong {
  display: block;
}

.admin-welcome-date small {
  color: #b9ddf5;
  font-size: 10px;
  text-transform: uppercase;
}

.admin-welcome-date strong {
  font-size: 12px;
  text-transform: capitalize;
}

.dashboard-stat-card {
  box-shadow: 0 7px 20px rgba(33, 53, 75, 0.08);
  position: relative;
}

.dashboard-stat-card::before {
  border-radius: 12px 0 0 12px;
  content: "";
  inset: 0 auto 0 0;
  position: absolute;
  width: 4px;
}

.dashboard-stat-card .icon-wrapper {
  border-radius: 12px !important;
  height: 43px;
  width: 43px;
}

.dashboard-stat-card h5 {
  color: #102a43;
  font-size: 19px;
  font-weight: 750;
}

.dashboard-stat-blue::before { background: #2f80ed; }
.dashboard-stat-blue .icon-wrapper { background: #e8f2ff; color: #2f80ed; }
.dashboard-stat-violet::before { background: #7c5ce7; }
.dashboard-stat-violet .icon-wrapper { background: #f0edff; color: #7c5ce7; }
.dashboard-stat-green::before { background: #22a06b; }
.dashboard-stat-green .icon-wrapper { background: #e7f7ef; color: #168557; }
.dashboard-stat-orange::before { background: #f59e42; }
.dashboard-stat-orange .icon-wrapper { background: #fff1df; color: #dc7d1d; }

/* Secondary dashboard sections */
.content > .row:not(:first-child) .card-body > h6,
.content > .row:not(:first-child) .card-body > .d-flex > h6 {
  color: #19324d;
  font-size: 14px;
  font-weight: 750;
  letter-spacing: -.01em;
}

.content > .row:not(:first-child) .card-body > h6::before,
.content > .row:not(:first-child) .card-body > .d-flex > h6::before {
  background: #ff9f43;
  border-radius: 4px;
  content: "";
  display: inline-block;
  height: 16px;
  margin-right: 8px;
  vertical-align: -3px;
  width: 3px;
}

.kpi-card {
  background: linear-gradient(145deg, #f7f9fc, #eef3f8) !important;
  border: 1px solid #e5ebf2;
  border-radius: 11px !important;
  min-height: 70px;
  padding: 12px 14px !important;
}

.kpi-card strong {
  color: #16324f;
  font-size: 14px !important;
}

.payment-summary-card {
  border: 1px solid transparent;
  border-radius: 12px !important;
  padding: 14px 16px !important;
  position: relative;
}

.payment-summary-card::before {
  border-radius: 12px 0 0 12px;
  content: "";
  inset: 0 auto 0 0;
  position: absolute;
  width: 4px;
}

.payment-summary-card.bg-success-subtle {
  border-color: #cdebdc;
}
.payment-summary-card.bg-success-subtle::before { background: #22a06b; }
.payment-summary-card.bg-danger-subtle {
  border-color: #f5d0d5;
}
.payment-summary-card.bg-danger-subtle::before { background: #e25565; }
.payment-summary-card.bg-info-subtle {
  border-color: #cde8ef;
}
.payment-summary-card.bg-info-subtle::before { background: #2c9ab7; }

.payment-summary-card strong {
  color: #19324d;
  font-size: 16px;
}

.financial-card {
  background: #f7f9fc !important;
  border: 1px solid #e5ebf2;
  border-radius: 11px !important;
  padding: 12px !important;
}

.content .table-responsive {
  border: 1px solid #e8edf3;
  border-radius: 11px;
  overflow: hidden;
}

.content .table {
  margin: 0;
}

.content .table thead th {
  background: #f2f5f9 !important;
  border-bottom: 1px solid #dfe6ee;
  color: #60758b;
  font-size: 11px !important;
  font-weight: 700;
  letter-spacing: .025em;
  padding: 11px 12px;
  text-transform: uppercase;
}

.content .table tbody td {
  border-color: #edf1f5;
  padding: 11px 12px;
}

.activity-item {
  background: #f7f9fc !important;
  border: 1px solid #e8edf3;
  border-radius: 11px !important;
  padding: 12px !important;
}

.activity-item:hover {
  background: #fff8ef !important;
  border-color: #ffd8ad;
  box-shadow: none;
  transform: translateX(2px);
}

.apexcharts-canvas {
  margin: 0 auto;
}

.progress {
  border-radius: 20px;
  height: 8px !important;
}

.progress-bar {
  border-radius: 20px;
}

.card .badge {
  border-radius: 7px;
}

.revenue-chart-controls {
  align-items: flex-end;
  display: flex;
  gap: 10px;
}

.revenue-chart-controls label {
  margin: 0;
}

.revenue-chart-controls label > span {
  color: #7b8da1;
  display: block;
  font-size: 10px;
  font-weight: 650;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.revenue-chart-controls .form-select {
  border-color: #dce4ed;
  border-radius: 9px;
  min-width: 130px;
}

.revenue-chart-loading {
  align-items: center;
  display: flex;
  height: 290px;
  justify-content: center;
}

.content > .row > [class*="col-"] > .card {
  border: 1px solid #e8edf3 !important;
  box-shadow: 0 6px 20px rgba(33, 53, 75, 0.06) !important;
}

.table-hover tbody tr:hover {
  transform: none;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .content {
    padding: 16px 12px;
  }

  .dashboard-hero {
    display: block;
    padding: 22px 18px;
  }

  .dashboard-hero h2 {
    font-size: 1.4rem;
  }

  .dashboard-hero__side {
    min-width: 0;
    margin-top: 18px;
  }

  .quick-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  .quick-action {
    min-width: 0;
  }

  .dashboard-stat-card .card-body {
    padding: 14px !important;
  }

  .stat-arrow {
    display: none;
  }

  .admin-welcome {
    align-items: flex-start;
    flex-direction: column;
    gap: 18px;
    padding: 20px;
  }

  .admin-welcome-date {
    min-width: 0;
    width: 100%;
  }

  .revenue-chart-controls {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .revenue-chart-controls .form-select {
    width: 100%;
  }

  .card-body {
    padding: 1rem !important;
  }

  .table-responsive {
    font-size: 0.875rem;
  }

  .card-hover:hover {
    transform: none;
  }

  .icon-pulse {
    animation: none;
  }
}

@media (max-width: 420px) {
  .quick-actions {
    grid-template-columns: 1fr;
  }
}

/* Smooth scroll behavior */
html {
  scroll-behavior: smooth;
}

/* Page wrapper fade in */
.page-wrapper {
  animation: fadeIn 0.5s ease-in;
}
</style>
